import { mergeImageCrop } from '@/helpers/image.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import type { FileInfo } from '@/services/types/file.types';
import { useBlocksStore } from '@/stores';

/**
 * Saves and clears a page block's image. A page block has no image route: its image is a form field like the others,
 * which the block's Save stores. A new file is uploaded to `/files` for the `FileInfo` the form holds. A crop alone
 * carries only its coordinates and size, merged into the form's image. `image` reads the form's image, `setImage`
 * writes it.
 */
export const usePageBlockImage = (
  image: () => FileInfo | null | undefined,
  setImage: (image: FileInfo | null) => void,
) => {
  const blocksStore = useBlocksStore();

  const save = async (options: SavingOptions) => {
    if (options.files?.length) {
      return wrapSavingOperation(async () => {
        const uploaded = await blocksStore.uploadPageBlockImage(options);
        if (!uploaded) return false;
        setImage(uploaded);
        return true;
      });
    }
    const current = image();
    if (current && options.metadata) setImage(mergeImageCrop(current, options.metadata));
  };

  return { save, clear: () => setImage(null) };
};
